import React from 'react';
import { Lock, Sparkles, ArrowRight, Layers, MonitorPlay, FileText, Brain, SquareGantt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAnalytics } from '@/hooks/use-analytics';

interface UpgradePromptProps {
  title?: string;
  description?: string;
  buttonText?: string;
  feature?: 'flashcards' | 'courses' | 'notes' | 'outlines' | 'questions';
  className?: string;
  onUpgradeClick?: () => void;
  variant?: 'modal' | 'inline' | 'overlay';
}

export function UpgradePrompt({
  title = 'Unlock Premium Features',
  description = 'Upgrade to access all content and features',
  buttonText = 'Unlock Now',
  feature,
  className,
  onUpgradeClick,
  variant = 'inline'
}: UpgradePromptProps) {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const handleUpgradeClick = () => {
    // Track the upgrade click event
    if (feature) {
      trackEvent('upgrade_prompt_clicked', {
        feature,
        variant,
        button_text: buttonText
      });
    }

    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      navigate('/pricing');
    }
  };

  // Contextual titles and descriptions based on feature
  const contextualContent = {
    flashcards: {
      title: 'Unlock All Flashcard Answers',
      description: 'Get unlimited access to study all flashcards with complete answers and explanations',
      buttonText: 'Unlock Flashcards'
    },
    courses: {
      title: 'Get Full Course Access',
      description: 'Unlock all course content, modules, and lessons to accelerate your legal education',
      buttonText: 'Access All Courses'
    },
    notes: {
      title: 'Unlock Smart Notes',
      description: 'Create and organize unlimited notes with AI-powered insights',
      buttonText: 'Unlock Notes'
    },
    outlines: {
      title: 'Access Outline IQ',
      description: 'Generate comprehensive outlines and study guides with AI assistance',
      buttonText: 'Unlock Outlines'
    },
    questions: {
      title: 'Unlock Question Bank',
      description: 'Access thousands of practice questions to test your knowledge',
      buttonText: 'Unlock Questions'
    }
  };

  const content = feature ? contextualContent[feature] : { title, description, buttonText };

  // Choose the appropriate icon based on feature - matching sidebar icons
  const getFeatureIcon = (size = 'h-6 w-6') => {
    const iconClass = `${size} text-jdorange`;
    switch (feature) {
      case 'flashcards':
        return <Layers className={iconClass} />;
      case 'courses':
        return <MonitorPlay className={iconClass} />;
      case 'notes':
        return <FileText className={iconClass} />;
      case 'questions':
        return <Brain className={iconClass} />;
      case 'outlines':
        return <SquareGantt className={iconClass} />;
      default:
        return <Lock className={iconClass} />;
    }
  };

  const getButtonIcon = () => {
    switch (feature) {
      case 'flashcards':
        return <Layers className="h-4 w-4 mr-2" />;
      case 'courses':
        return <MonitorPlay className="h-4 w-4 mr-2" />;
      case 'notes':
        return <FileText className="h-4 w-4 mr-2" />;
      case 'questions':
        return <Brain className="h-4 w-4 mr-2" />;
      case 'outlines':
        return <SquareGantt className="h-4 w-4 mr-2" />;
      default:
        return <Sparkles className="h-4 w-4 mr-2" />;
    }
  };

  if (variant === 'overlay') {
    return (
      <div className={cn(
        "absolute inset-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-lg flex items-center justify-center p-6 z-10",
        className
      )}>
        <div className="text-center space-y-4 max-w-sm">
          <div className="flex justify-center">
            <div className="h-12 w-12 bg-jdorange/10 dark:bg-jdorange/20 rounded-full flex items-center justify-center">
              {getFeatureIcon()}
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {content.title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {content.description}
          </p>
          <Button 
            onClick={handleUpgradeClick}
            className="w-full bg-jdorange hover:bg-jdorange/90 text-white group"
          >
            {getButtonIcon()}
            {content.buttonText}
            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    );
  }

  if (variant === 'modal') {
    return (
      <div className={cn(
        "bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md mx-auto",
        className
      )}>
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="h-16 w-16 bg-gradient-to-br from-jdorange/20 to-jdorange/10 dark:from-jdorange/30 dark:to-jdorange/20 rounded-full flex items-center justify-center">
              {getFeatureIcon('h-8 w-8')}
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {content.title}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {content.description}
          </p>
          <div className="pt-2">
            <Button 
              onClick={handleUpgradeClick}
              size="lg"
              className="w-full bg-gradient-to-r from-jdorange to-jdorange/90 hover:from-jdorange/90 hover:to-jdorange text-white group"
            >
              {getButtonIcon()}
              {content.buttonText}
              <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Default inline variant
  return (
    <div className={cn(
      "flex items-center justify-between p-4 bg-jdorange/5 dark:bg-jdorange/10 rounded-lg border border-jdorange/20",
      className
    )}>
      <div className="flex items-center gap-3">
        <Lock className="h-5 w-5 text-jdorange" />
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100">{content.title}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{content.description}</p>
        </div>
      </div>
      <Button 
        onClick={handleUpgradeClick}
        className="bg-jdorange hover:bg-jdorange/90 text-white group"
      >
        {content.buttonText}
        <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
      </Button>
    </div>
  );
}